import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  activateUser,
  createUser,
  deactivateUser,
  listUsers,
  resetUserPassword,
  updateUser,
} from '@/api/users'
import Pagination from '@/components/Pagination'
import { formatKstDateTime } from '@/utils/datetime'
import type { ApiError, ManagedUser, UpdateManagedUserRequest, UserRole } from '@/types'

const PAGE_SIZE = 25
const USERNAME_PATTERN = /^[a-z][a-z0-9_]{2,31}$/
type RoleCategory = 'all' | UserRole
const ROLE_CATEGORIES: Array<{ value: RoleCategory; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'admin', label: 'Admin' },
  { value: 'dev', label: 'Dev' },
  { value: 'operator', label: 'Operator' },
]

interface GeneratedPassword {
  username: string
  password: string
}

export default function UsersPage() {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [page, setPage] = useState(1)
  const [roleCategory, setRoleCategory] = useState<RoleCategory>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null)
  const [generatedPassword, setGeneratedPassword] = useState<GeneratedPassword | null>(null)
  const [actionUserId, setActionUserId] = useState<string | null>(null)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const response = await listUsers({
        limit: 1000,
        offset: 0,
      })
      setUsers(response.items)
      setError(null)
    } catch (caught: unknown) {
      setError((caught as ApiError)?.message ?? 'Failed to load users.')
    } finally {
      setLoading(false)
    }
  }, [])

  const categoryCounts = useMemo(
    () => ({
      all: users.length,
      admin: users.filter((user) => user.role === 'admin').length,
      dev: users.filter((user) => user.role === 'dev').length,
      operator: users.filter((user) => user.role === 'operator').length,
    }),
    [users],
  )
  const filteredUsers = useMemo(
    () => roleCategory === 'all'
      ? users
      : users.filter((user) => user.role === roleCategory),
    [roleCategory, users],
  )
  const total = filteredUsers.length
  const visibleUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
    if (!loading && page > lastPage) setPage(lastPage)
  }, [loading, page, total])

  function showToast(message: string) {
    setToast(message)
    window.setTimeout(() => setToast(null), 3500)
  }

  async function handleCreate(input: {
    username: string
    displayName: string
    role: Exclude<UserRole, 'admin'>
  }) {
    const response = await createUser({
      username: input.username,
      display_name: input.displayName,
      role: input.role,
    })
    setShowCreate(false)
    setGeneratedPassword({
      username: response.user.username,
      password: response.generated_password,
    })
    showToast(`User ${response.user.username} created.`)
    await loadUsers()
  }

  async function handleEdit(input: {
    username: string
    displayName: string
    role: Exclude<UserRole, 'admin'>
  }) {
    if (!editingUser) return
    const body: UpdateManagedUserRequest = {}
    const nextDisplayName = input.displayName.trim() || null
    if (nextDisplayName !== editingUser.display_name) body.display_name = nextDisplayName
    if (input.role !== editingUser.role) body.role = input.role
    if (Object.keys(body).length > 0) {
      await updateUser(editingUser.id, body)
    }
    showToast(`User ${editingUser.username} updated.`)
    setEditingUser(null)
    await loadUsers()
  }

  async function handleActiveChange(user: ManagedUser) {
    const verb = user.is_active ? 'deactivate' : 'activate'
    if (!window.confirm(`${verb === 'deactivate' ? 'Deactivate' : 'Activate'} ${user.username}?`)) return
    setActionUserId(user.id)
    try {
      if (user.is_active) {
        await deactivateUser(user.id)
      } else {
        await activateUser(user.id)
      }
      showToast(`User ${user.username} ${verb}d.`)
      await loadUsers()
    } catch (caught: unknown) {
      showToast(`${verb} failed: ${(caught as ApiError)?.message ?? 'unknown error'}`)
    } finally {
      setActionUserId(null)
    }
  }

  async function handleResetPassword(user: ManagedUser) {
    if (!window.confirm(`Reset the password for ${user.username}? All existing sessions will expire.`)) return
    setActionUserId(user.id)
    try {
      const response = await resetUserPassword(user.id)
      setGeneratedPassword({
        username: user.username,
        password: response.generated_password,
      })
      showToast(`Password reset for ${user.username}.`)
      await loadUsers()
    } catch (caught: unknown) {
      showToast(`Password reset failed: ${(caught as ApiError)?.message ?? 'unknown error'}`)
    } finally {
      setActionUserId(null)
    }
  }

  return (
    <div>
      <div style={pageHeaderStyle}>
        <div>
          <h1 style={pageTitleStyle}>Users</h1>
          <p style={pageSubtitleStyle}>Manage personal dev and operator accounts.</p>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} style={primaryButtonStyle}>
          Create user
        </button>
      </div>

      {error && (
        <div style={errorBannerStyle}>
          {error}
          <button type="button" onClick={loadUsers} style={inlineButtonStyle}>Retry</button>
        </div>
      )}

      <div role="tablist" aria-label="Filter users by role" style={categoryTabsStyle}>
        {ROLE_CATEGORIES.map((category) => {
          const selected = roleCategory === category.value
          return (
            <button
              key={category.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                setRoleCategory(category.value)
                setPage(1)
              }}
              style={selected ? activeCategoryTabStyle : categoryTabStyle}
            >
              {category.label}
              <span style={selected ? activeCategoryCountStyle : categoryCountStyle}>
                {categoryCounts[category.value]}
              </span>
            </button>
          )
        })}
      </div>

      <div style={cardStyle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Last login</th>
                <th style={thStyle}>Password changed</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={7} style={emptyCellStyle}>
                    {roleCategory === 'all'
                      ? 'No users found.'
                      : `No ${roleCategory} users found.`}
                  </td>
                </tr>
              )}
              {visibleUsers.map((user) => {
                const busy = actionUserId === user.id
                const protectedAdmin = user.role === 'admin'
                return (
                  <tr key={user.id}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{user.display_name || user.username}</div>
                      <div style={secondaryTextStyle}>{user.username}</div>
                    </td>
                    <td style={tdStyle}><RoleBadge role={user.role} /></td>
                    <td style={tdStyle}>
                      <span style={user.is_active ? activeBadgeStyle : inactiveBadgeStyle}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={tdStyle}>{formatKstDateTime(user.last_login_at, '—')}</td>
                    <td style={tdStyle}>{formatKstDateTime(user.password_changed_at, '—')}</td>
                    <td style={tdStyle}>{formatKstDateTime(user.created_at, '—')}</td>
                    <td style={tdStyle}>
                      {protectedAdmin ? (
                        <span style={secondaryTextStyle}>Protected system admin</span>
                      ) : (
                        <div style={actionGroupStyle}>
                          <button
                            type="button"
                            onClick={() => setEditingUser(user)}
                            disabled={busy}
                            style={smallButtonStyle}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResetPassword(user)}
                            disabled={busy}
                            style={smallButtonStyle}
                          >
                            Reset password
                          </button>
                          <button
                            type="button"
                            onClick={() => handleActiveChange(user)}
                            disabled={busy}
                            style={user.is_active ? dangerSmallButtonStyle : smallButtonStyle}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
              {loading && (
                <tr>
                  <td colSpan={7} style={emptyCellStyle}>Loading users...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!loading && !error && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            itemLabel="users"
            onPageChange={setPage}
          />
        )}
      </div>

      {showCreate && (
        <UserFormModal
          title="Create user"
          submitLabel="Create"
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}

      {editingUser && editingUser.role !== 'admin' && (
        <UserFormModal
          title={`Edit ${editingUser.username}`}
          submitLabel="Save"
          initialDisplayName={editingUser.display_name ?? ''}
          initialRole={editingUser.role}
          hideUsername
          onClose={() => setEditingUser(null)}
          onSubmit={handleEdit}
        />
      )}

      {generatedPassword && (
        <GeneratedPasswordModal
          value={generatedPassword}
          onClose={() => setGeneratedPassword(null)}
        />
      )}

      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  )
}

interface UserFormModalProps {
  title: string
  submitLabel: string
  initialDisplayName?: string
  initialRole?: Exclude<UserRole, 'admin'>
  hideUsername?: boolean
  onClose: () => void
  onSubmit: (input: {
    username: string
    displayName: string
    role: Exclude<UserRole, 'admin'>
  }) => Promise<void>
}

function UserFormModal({
  title,
  submitLabel,
  initialDisplayName = '',
  initialRole = 'operator',
  hideUsername = false,
  onClose,
  onSubmit,
}: UserFormModalProps) {
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState(initialDisplayName)
  const [role, setRole] = useState<Exclude<UserRole, 'admin'>>(initialRole)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const normalizedUsername = username.trim().toLowerCase()
    if (!hideUsername && !USERNAME_PATTERN.test(normalizedUsername)) {
      setError('Username must match: lowercase letter first, then 3-32 lowercase letters, digits, or underscores.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        username: hideUsername ? '' : normalizedUsername,
        displayName,
        role,
      })
    } catch (caught: unknown) {
      setError((caught as ApiError)?.message ?? 'Unable to save user.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={backdropStyle} onClick={(event) => {
      if (event.target === event.currentTarget && !submitting) onClose()
    }}>
      <div role="dialog" aria-modal="true" aria-label={title} style={modalStyle}>
        <h2 style={modalTitleStyle}>{title}</h2>
        <form onSubmit={handleSubmit}>
          {!hideUsername && (
            <label style={labelStyle}>
              Username
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value.toLowerCase())}
                autoComplete="off"
                placeholder="sm_kim"
                maxLength={32}
                required
                style={inputStyle}
              />
              <span style={helpTextStyle}>3-32 lowercase letters, digits, and underscores.</span>
            </label>
          )}
          <label style={labelStyle}>
            Display name (optional)
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={255}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Role
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as Exclude<UserRole, 'admin'>)}
              style={inputStyle}
            >
              <option value="operator">Operator</option>
              <option value="dev">Dev</option>
            </select>
          </label>
          {error && <div style={formErrorStyle}>{error}</div>}
          <div style={modalActionsStyle}>
            <button type="button" onClick={onClose} disabled={submitting} style={secondaryButtonStyle}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={primaryButtonStyle}>
              {submitting ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function GeneratedPasswordModal({
  value,
  onClose,
}: {
  value: GeneratedPassword
  onClose: () => void
}) {
  const [copyMessage, setCopyMessage] = useState<string | null>(null)

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(value.password)
      setCopyMessage('Copied.')
    } catch {
      setCopyMessage('Copy failed. Select and copy the password manually.')
    }
  }

  return (
    <div style={backdropStyle}>
      <div role="dialog" aria-modal="true" aria-label="Generated password" style={modalStyle}>
        <h2 style={modalTitleStyle}>Generated password</h2>
        <div style={warningStyle}>
          This password is shown only once. Transfer it through an approved secure channel before closing.
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={fieldNameStyle}>Username</div>
          <code>{value.username}</code>
        </div>
        <label style={labelStyle}>
          Password
          <input
            readOnly
            value={value.password}
            onFocus={(event) => event.currentTarget.select()}
            style={{ ...inputStyle, fontFamily: 'monospace' }}
          />
        </label>
        {copyMessage && <div style={helpTextStyle}>{copyMessage}</div>}
        <div style={modalActionsStyle}>
          <button type="button" onClick={copyPassword} style={secondaryButtonStyle}>Copy</button>
          <button type="button" onClick={onClose} style={primaryButtonStyle}>I saved it — close</button>
        </div>
      </div>
    </div>
  )
}

function RoleBadge({ role }: { role: UserRole }) {
  const colors: Record<UserRole, { color: string; background: string }> = {
    admin: { color: '#6a1b9a', background: '#f3e5f5' },
    dev: { color: '#1565c0', background: '#e3f2fd' },
    operator: { color: '#2e7d32', background: '#e8f5e9' },
  }
  return <span style={{ ...badgeBaseStyle, ...colors[role] }}>{role}</span>
}

const pageHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }
const pageTitleStyle: React.CSSProperties = { margin: 0, fontSize: 22, fontWeight: 700 }
const pageSubtitleStyle: React.CSSProperties = { margin: '5px 0 0', color: '#777', fontSize: 13 }
const categoryTabsStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }
const categoryTabStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 34, padding: '6px 12px', border: '1px solid #d8d8df', borderRadius: 18, background: '#fff', color: '#555', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600 }
const activeCategoryTabStyle: React.CSSProperties = { ...categoryTabStyle, border: '1px solid #3949ab', background: '#3949ab', color: '#fff' }
const categoryCountStyle: React.CSSProperties = { minWidth: 20, padding: '1px 6px', borderRadius: 10, background: '#eeeeef', color: '#666', fontSize: 10, textAlign: 'center' }
const activeCategoryCountStyle: React.CSSProperties = { ...categoryCountStyle, background: 'rgba(255,255,255,.2)', color: '#fff' }
const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: 16 }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '9px 10px', borderBottom: '2px solid #e0e0e0', color: '#555', whiteSpace: 'nowrap' }
const tdStyle: React.CSSProperties = { padding: '10px', borderBottom: '1px solid #eee', verticalAlign: 'middle' }
const emptyCellStyle: React.CSSProperties = { ...tdStyle, textAlign: 'center', color: '#888', padding: 28 }
const secondaryTextStyle: React.CSSProperties = { color: '#888', fontSize: 11, marginTop: 2 }
const badgeBaseStyle: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }
const activeBadgeStyle: React.CSSProperties = { ...badgeBaseStyle, color: '#2e7d32', background: '#e8f5e9' }
const inactiveBadgeStyle: React.CSSProperties = { ...badgeBaseStyle, color: '#666', background: '#eeeeee' }
const actionGroupStyle: React.CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap' }
const primaryButtonStyle: React.CSSProperties = { padding: '7px 14px', border: 'none', borderRadius: 4, background: '#3949ab', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }
const secondaryButtonStyle: React.CSSProperties = { padding: '7px 14px', border: '1px solid #ccc', borderRadius: 4, background: '#fff', color: '#444', cursor: 'pointer', fontFamily: 'inherit' }
const smallButtonStyle: React.CSSProperties = { ...secondaryButtonStyle, padding: '4px 8px', fontSize: 11 }
const dangerSmallButtonStyle: React.CSSProperties = { ...smallButtonStyle, color: '#c62828', borderColor: '#ef9a9a' }
const inlineButtonStyle: React.CSSProperties = { marginLeft: 12, border: 'none', background: 'transparent', color: '#3949ab', cursor: 'pointer' }
const errorBannerStyle: React.CSSProperties = { marginBottom: 16, padding: '10px 12px', background: '#fdecea', color: '#c62828', borderRadius: 4, fontSize: 13 }
const toastStyle: React.CSSProperties = { position: 'fixed', right: 24, bottom: 24, zIndex: 1100, padding: '10px 16px', background: '#263238', color: '#fff', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,.25)', fontSize: 13 }
const backdropStyle: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(0,0,0,.45)' }
const modalStyle: React.CSSProperties = { width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: 24, background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,.25)' }
const modalTitleStyle: React.CSSProperties = { margin: '0 0 18px', fontSize: 19 }
const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14, color: '#444', fontSize: 13, fontWeight: 600 }
const inputStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontFamily: 'inherit', fontSize: 13 }
const helpTextStyle: React.CSSProperties = { color: '#777', fontSize: 11, fontWeight: 400 }
const fieldNameStyle: React.CSSProperties = { marginBottom: 4, color: '#777', fontSize: 11, textTransform: 'uppercase' }
const formErrorStyle: React.CSSProperties = { marginBottom: 12, padding: 9, borderRadius: 4, background: '#fdecea', color: '#c62828', fontSize: 12 }
const modalActionsStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }
const warningStyle: React.CSSProperties = { marginBottom: 16, padding: '10px 12px', border: '1px solid #ffe082', borderRadius: 4, color: '#8a5a00', background: '#fff8e1', fontSize: 13, lineHeight: 1.45 }
